import { Button } from '../button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../ui/command'
import { Input } from '../../ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover'
import { ScrollArea } from '../../ui/scroll-area'
import { cn } from '../../../lib/utils'
import parsePhoneNumberFromString, {
  AsYouType,
  type CarrierCode,
  type CountryCallingCode,
  type E164Number,
  type NationalNumber,
  type CountryCode,
  type NumberType,
} from 'libphonenumber-js'
import { ChevronsUpDown } from 'lucide-react'
import * as React from 'react'
import { countries } from './countries'
import { useStateHistory } from './use-state-history'
import { Separator } from '../separator'

export type Country = (typeof countries)[number]

export type PhoneData = {
  phoneNumber?: E164Number
  countryCode?: CountryCode
  countryCallingCode?: CountryCallingCode
  carrierCode?: CarrierCode
  nationalNumber?: NationalNumber
  internationalNumber?: string
  possibleCountries?: string
  isValid?: boolean
  isPossible?: boolean
  uri?: string
  type?: NumberType
}

interface PhoneInputProps
  extends Omit<React.ComponentPropsWithoutRef<'input'>, 'onChange'> {
  value?: string
  defaultCountry?: CountryCode
  label?: string
  onChange?: (value: string) => void
  inputClassName?: string
}

export function getPhoneData(phone?: string): PhoneData {
  if (!phone) {
    return {
      isValid: false,
    }
  }
  const asYouType = new AsYouType()
  asYouType.input(phone)
  const number = asYouType.getNumber()
  return {
    phoneNumber: number?.number,
    countryCode: number?.country,
    countryCallingCode: number?.countryCallingCode,
    carrierCode: number?.carrierCode,
    nationalNumber: number?.nationalNumber,
    internationalNumber: number?.formatInternational(),
    possibleCountries: number?.getPossibleCountries().join(', '),
    isValid: number?.isValid(),
    isPossible: number?.isPossible(),
    uri: number?.getURI(),
    type: number?.getType(),
  }
}

export function PhoneInput({
  value: valueProp,
  defaultCountry = 'US',
  className,
  id,
  required = true,
  label,
  onChange,
  inputClassName,
  disabled = false,
  ...rest
}: PhoneInputProps) {
  const asYouType = new AsYouType()

  const inputRef = React.useRef<HTMLInputElement>(null)

  const [value, handlers, history] = useStateHistory(valueProp)

  if (value && value.length > 0) {
    defaultCountry =
      parsePhoneNumberFromString(value)?.getPossibleCountries()[0] ||
      defaultCountry
  }

  const [openCommand, setOpenCommand] = React.useState(false)
  const [countryCode, setCountryCode] =
    React.useState<CountryCode>(defaultCountry)

  const selectedCountry = countries.find(
    (country) => country.iso2 === countryCode
  )

  const defaultCountryInfo = countries.find(
    (country) => country.iso2 === defaultCountry
  )

  const initializeDefaultValue = () => {
    if (value) {
      return value
    }

    return `+${selectedCountry?.phone_code}`
  }

  const handleOnInput = (event: React.FormEvent<HTMLInputElement>) => {
    asYouType.reset()

    const value = event.currentTarget.value

    const formattedValue = asYouType.input(value)
    const number = asYouType.getNumber()

    const countryCodeFromPhoneInput = number?.country || defaultCountry
    setCountryCode(countryCodeFromPhoneInput)
    event.currentTarget.value = formattedValue
    handlers.set(formattedValue)

    onChange?.(`+${number?.countryCallingCode}${number?.nationalNumber}`)
  }

  const handleOnPaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault()
    asYouType.reset()

    const clipboardData = event.clipboardData

    if (clipboardData) {
      const pastedData = clipboardData.getData('text/plain')
      const formattedValue = asYouType.input(pastedData)
      const number = asYouType.getNumber()
      const countryCodeFromPhoneInput = number?.country || defaultCountry
      setCountryCode(countryCodeFromPhoneInput)
      event.currentTarget.value = formattedValue
      handlers.set(formattedValue)

      onChange?.(`+${number?.countryCallingCode}${number?.nationalNumber}`)
    }
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if ((event.metaKey || event.ctrlKey) && event.key === 'z') {
      handlers.back()
      if (
        inputRef.current &&
        history.current > 0 &&
        history.history[history.current - 1] !== undefined
      ) {
        event.preventDefault()
        inputRef.current.value = history.history[history.current - 1] || ''
      }
    }
  }

  const handleSelectCountry = (country: Country) => {
    if (inputRef.current) {
      const number = asYouType.getNumber()

      inputRef.current.value = `+${country.phone_code}${
        number?.nationalNumber || ''
      }`
      handlers.set(`+${country.phone_code}${number?.nationalNumber || ''}`)
      onChange?.(`+${country.phone_code}${number?.nationalNumber || ''}`)
      inputRef.current.focus()
    }
    setCountryCode(country.iso2 as CountryCode)
    setOpenCommand(false)
  }

  return (
    <div className={cn('flex min-h-[55px]', className)}>
      <Popover open={openCommand} onOpenChange={setOpenCommand} modal={true}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={openCommand}
            className={cn(
              'h-full w-max items-center justify-between whitespace-nowrap rounded-bl-lg rounded-br-none rounded-tl-lg rounded-tr-none bg-white text-lg leading-5 text-[#515151] !outline-none focus-visible:ring-0 disabled:opacity-100',
              disabled ? 'bg-[#0000000d]' : ''
            )}
            disabled={disabled}
          >
            {selectedCountry?.name ? (
              <span className="relative top-0.5">{selectedCountry.emoji}</span>
            ) : (
              'Select country'
            )}
            <ChevronsUpDown className="size-4 shrink-0 text-[#515151] opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-max bg-white p-0" align="start">
          <Command>
            <CommandInput
              className="border-0 outline-none"
              placeholder="Search country..."
            />
            <CommandList>
              <CommandEmpty>No country found.</CommandEmpty>
              <ScrollArea
                className={
                  '[&>[data-radix-scroll-area-viewport]]:max-h-[300px]'
                }
              >
                <CommandGroup>
                  {defaultCountryInfo && (
                    <>
                      <CommandItem
                        value={`${defaultCountryInfo.name} (+${defaultCountryInfo.phone_code})`}
                        onSelect={() => handleSelectCountry(defaultCountryInfo)}
                        className="bg-black-opacity-8"
                      >
                        <span className="relative -top-1 mr-2 h-3 w-4 object-cover">
                          {defaultCountryInfo.emoji}
                        </span>
                        {defaultCountryInfo.name}
                        <span className="text-gray-11 ml-1">
                          (+{defaultCountryInfo.phone_code})
                        </span>
                      </CommandItem>
                      <Separator className="p-0" />
                    </>
                  )}
                  {countries
                    .filter(
                      (country) => country.name !== defaultCountryInfo?.name
                    )
                    .map((country) => {
                      return (
                        <CommandItem
                          key={country.iso3}
                          value={`${country.name} (+${country.phone_code})`}
                          onSelect={() => handleSelectCountry(country)}
                        >
                          <span className="relative -top-1 mr-2 h-3 w-4 object-cover">
                            {country.emoji}
                          </span>
                          {country.name}
                          <span className="text-gray-11 ml-1">
                            (+{country.phone_code})
                          </span>
                        </CommandItem>
                      )
                    })}
                </CommandGroup>
              </ScrollArea>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <Input
        type="text"
        pattern="^(\+)?[0-9\s]*$"
        id={id}
        placeholder={label || 'Phone'}
        defaultValue={initializeDefaultValue()}
        onInput={handleOnInput}
        onPaste={handleOnPaste}
        onKeyDown={handleKeyDown}
        required={required}
        aria-required={required}
        className={cn(
          'h-full rounded-bl-none rounded-br-lg rounded-tl-none rounded-tr-lg bg-white text-[#515151]',
          inputClassName,
          disabled ? 'bg-[#f6f6f6]' : ''
        )}
        disabled={disabled}
        {...rest}
        ref={inputRef}
      />
    </div>
  )
}

export * from 'libphonenumber-js/max'
